import { invariantResponse } from '@epic-web/invariant'
import { useTranslation } from 'react-i18next'
import { type ActionFunctionArgs, data, Form, Link  } from 'react-router'
import { PageHeader } from '#app/components/page-header.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'

export async function action({ request }: ActionFunctionArgs) {
	const userId = await requireUserId(request)
	const formData = await request.formData()

	const name = formData.get('name') as string
	const kvkNumber = formData.get('kvkNumber') as string | null
	const website = formData.get('website') as string | null
	const phone = formData.get('phone') as string | null
	const streetAddress = formData.get('streetAddress') as string | null
	const city = formData.get('city') as string | null
	const state = formData.get('state') as string | null
	const country = formData.get('country') as string | null
	const postalCode = formData.get('postalCode') as string | null
	const notes = formData.get('notes') as string | null

	invariantResponse(name, 'Name is required')

	const organization = await prisma.organization.create({
		data: {
			name,
			kvkNumber: kvkNumber || undefined,
			website: website || undefined,
			phone: phone || undefined,
			address: streetAddress || undefined,
			city: city || undefined,
			state: state || undefined,
			country: country || undefined,
			postalCode: postalCode || undefined,
			notes: notes || undefined,
			userId,
		},
	})

	// Check if this is an API request from the drawer
	const acceptHeader = request.headers.get('accept')
	if (acceptHeader?.includes('application/json')) {
		return data({ success: true, organization })
	}
	
	// No organization detail route exists yet — land on the list with confirmation
	return redirectWithToast('/organizations', {
		title: 'Organization created',
		description: organization.name,
	})
}

export default function NewOrganizationRoute() {
	const { t } = useTranslation(['app', 'common'])

	return (
		<div className="flex h-full flex-col">
			<PageHeader
				title={t('app:organizations.newOrganization')}
				breadcrumbs={[
					{ label: t('app:navigation.organizations'), href: '/organizations' },
					{ label: t('app:organizations.newOrganization') },
				]}
			/>

			<div className="flex-1 overflow-y-auto p-6">
				<Form method="post" className="mx-auto max-w-2xl">
					<Card>
						<CardHeader>
							<CardTitle>{t('app:organizations.organizationDetails')}</CardTitle>
							<CardDescription>
								{t('app:organizations.organizationDetailsDescription')}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							<div>
								<Label htmlFor="name">
									{t('app:organizations.name')} <span className="text-destructive">*</span>
								</Label>
								<Input
									id="name"
									name="name"
									required
									placeholder={t('app:organizations.namePlaceholder')}
									className="mt-1"
								/>
							</div>

							<div>
								<Label htmlFor="website">{t('app:organizations.website')}</Label>
								<Input
									id="website"
									name="website"
									type="url"
									placeholder="https://example.com"
									className="mt-1"
								/>
							</div>

							<div>
								<Label htmlFor="phone">{t('app:organizations.phone')}</Label>
								<Input
									id="phone"
									name="phone"
									type="tel"
									placeholder="+31 20 123 4567"
									className="mt-1"
								/>
							</div>

							<div className="space-y-4">
								<h3 className="text-sm font-medium">{t('app:organizations.address')}</h3>
								
								<div>
									<Label htmlFor="address">{t('app:organizations.streetAddress')}</Label>
									<Input
										id="address"
										name="address"
										placeholder={t('app:organizations.streetAddressPlaceholder')}
										className="mt-1"
									/>
								</div>

								<div className="grid grid-cols-2 gap-4">
									<div>
										<Label htmlFor="city">{t('app:organizations.city')}</Label>
										<Input
											id="city"
											name="city"
											placeholder={t('app:organizations.cityPlaceholder')}
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="state">{t('app:organizations.state')}</Label>
										<Input
											id="state"
											name="state"
											placeholder={t('app:organizations.statePlaceholder')}
											className="mt-1"
										/>
									</div>
								</div>

								<div className="grid grid-cols-2 gap-4">
									<div>
										<Label htmlFor="country">{t('app:organizations.country')}</Label>
										<Input
											id="country"
											name="country"
											placeholder={t('app:organizations.countryPlaceholder')}
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="postalCode">{t('app:organizations.postalCode')}</Label>
										<Input
											id="postalCode"
											name="postalCode"
											placeholder="1234 AB"
											className="mt-1"
										/>
									</div>
								</div>
							</div>

							<div>
								<Label htmlFor="notes">{t('app:organizations.notes')}</Label>
								<Textarea
									id="notes"
									name="notes"
									rows={4}
									placeholder={t('app:organizations.notesPlaceholder')}
									className="mt-1"
								/>
							</div>

							<div className="flex gap-4 pt-4">
								<Button type="submit" className="flex-1">
									<Icon name="check" className="mr-2" />
									{t('app:organizations.createOrganization')}
								</Button>
								<Link to="/organizations" className="flex-1">
									<Button type="button" variant="outline" className="w-full">
										{t('common:cancel')}
									</Button>
								</Link>
							</div>
						</CardContent>
					</Card>
				</Form>
			</div>
		</div>
	)
}